<template>
  <div class="ssh-terminal-container">
    <div ref="terminal" class="synpse-xterm"></div>
  </div>
</template>

<style scoped>
.ssh-terminal-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.synpse-xterm {
  padding: 8px;
  height: 100%;
  width: 100%;
  border: none;
}
</style>

<script>
import { Terminal } from "xterm";
import * as fit from "xterm/lib/addons/fit/fit";
import { Base64 } from "js-base64";
import * as webLinks from "xterm/lib/addons/webLinks/webLinks";
import * as search from "xterm/lib/addons/search/search";
import "xterm/dist/xterm.css"

const solarizedDarkTheme = {
  foreground: '#d2d2d2',
  background: '#2b2b2b',
  cursor: '#adadad',
  black: '#000000',
  red: '#d81e00',
  green: '#5ea702',
  yellow: '#cfae00',
  blue: '#427ab3',
  magenta: '#89658e',
  cyan: '#00a7aa',
  white: '#dbded8',
  brightBlack: '#686a66',
  brightRed: '#f54235',
};

let bindTerminalResize = (term, websocket) => {
  let onTermResize = size => {
    websocket.send(
      JSON.stringify({
        type: "resize",
        rows: size.rows,
        cols: size.cols,
      })
    )
  }
  term.on("resize", onTermResize)
  websocket.addEventListener("close", function () {
    term.off("resize", onTermResize)
  });
};

let bindTerminal = (term, websocket, bidirectional, bufferedTime) => {
  term.socket = websocket
  let messageBuffer = null
  let handleWebSocketMessage = function (ev) {    
    if (bufferedTime && bufferedTime > 0) {
      if (messageBuffer) {
        messageBuffer += ev.data
      } else {
        messageBuffer = ev.data
        setTimeout(function () {
          term.write(messageBuffer)
        }, bufferedTime)
      }
    } else {
      term.write(ev.data)
    }
  };
  let handleTerminalData = function (data) {
    websocket.send(
      JSON.stringify({
        type: "cmd",
        cmd: Base64.encode(data)
      })
    );
  };
  websocket.onmessage = handleWebSocketMessage;
  if (bidirectional) {
    term.on("data", handleTerminalData);
  }
  let heartBeatTimer = setInterval(function () {
    websocket.send(JSON.stringify({ type: "heartbeat", data: "" }))
  }, 20 * 1000);
  websocket.addEventListener("close", function () {
    websocket.removeEventListener("message", handleWebSocketMessage)
    term.off("data", handleTerminalData)
    delete term.socket
    clearInterval(heartBeatTimer)
  });
};

export default {
  props: {
    session: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      ws: null,
      term: null,
      isInitialized: false
    }
  },
  computed: {
    wsBase() {
      return `${window.location.protocol === "https:" ? "wss://" : "ws://"}${window.location.host}`;
    },
    wsUrl() {
      let token = this.$store.getters.jwt
      if (!this.term) {
        return ''
      }
      return `${this.wsBase}/api/projects/${this.session.projectId}/devices/${this.session.deviceId}/ssh?cols=${this.term.cols}&rows=${this.term.rows}&_t=${token}`
    }
  },
  watch: {
    isActive(newVal) {
      if (newVal && this.term && !this.isInitialized) {
        this.$nextTick(() => {
          this.initTerminal()
        })
      }
    }
  },
  mounted() {
    if (this.isActive) {
      this.$nextTick(() => {
        this.initTerminal()
      })
    }
  },
  beforeDestroy() {
    this.cleanup()
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    onWindowResize() {
      if (this.term) {
        this.term.fit();
      }
    },
    resize() {
      if (this.term) {
        this.$nextTick(() => {
          this.term.fit();
        });
      }
    },
    doLink(ev, url) {
      if (ev.type === 'click') {
        window.open(url)
      }
    },
    cleanup() {
      window.removeEventListener("resize", this.onWindowResize)
      if (this.ws) {
        this.ws.close()
      }
      if (this.term) {
        this.term.dispose()
      }
      this.isInitialized = false
    },
    initTerminal() {
      if (this.isInitialized) {
        return
      }

      Terminal.applyAddon(fit)
      Terminal.applyAddon(webLinks)
      Terminal.applyAddon(search)

      this.term = new Terminal({
        fontSize: 14,
        cursorBlink: true,
        cursorStyle: 'bar',
        bellStyle: "sound",
        fontFamily: '"Menlo for Powerline", Menlo, Consolas, "Liberation Mono", Courier, monospace',
        theme: solarizedDarkTheme
      })
      
      this.term.open(this.$refs.terminal)
      this.term.fit()

      this.term.webLinksInit(this.doLink)
      window.addEventListener("resize", this.onWindowResize)
      
      this.ws = new WebSocket(this.wsUrl)
      
      this.ws.onclose = (ce) => {        
        this.term.setOption("cursorBlink", false);
        if (ce.code !== 1005) {
          this.$store.dispatch('Notify', `Error code'${ce.code}': ${ce.reason}`)
          this.cleanup()
        }
      };
      
      bindTerminal(this.term, this.ws, true, -1)
      bindTerminalResize(this.term, this.ws)
      
      this.isInitialized = true
    }
  }
}
</script>

